import {
  type InventoryItem,
  inventoryService,
} from "../services/inventoryService";
import { useMutation, useQuery, useQueryClient } from "./useDataCache";
import { useUser } from "./useUser";

// Hook to load and keep inventory in sync via realtime updates and optimistic mutations
export function useInventory(
  location?: "fridge" | "freezer" | "pantry" | "other" | "all",
) {
  const queryClient = useQueryClient();
  const { householdId, userId } = useUser();
  const queryKey = ["inventory", location, householdId];

  // Load inventory items for the given location and household
  const { data: items = [], ...queryInfo } = useQuery({
    queryKey,
    queryFn: () => {
      if (!householdId) return Promise.resolve([]);
      if (location === "all") {
        return inventoryService.getAllItems(householdId);
      }
      return inventoryService.getItems(
        location as "fridge" | "freezer" | "pantry" | "other",
        householdId,
      );
    },
    enabled: !!householdId,
  });

  // Mutations with optimistic updates: UI updates instantly, rolls back on error,
  // and re-syncs from the server once the mutation settles.
  const addItemMutation = useMutation({
    mutationFn: (
      item: Omit<
        InventoryItem,
        "id" | "created_at" | "user_id" | "updated_at" | "household_id"
      >,
    ) => {
      if (!householdId || !userId) throw new Error("Not authenticated");
      return inventoryService.addItem(
        item.item_name,
        item.quantity,
        item.unit,
        item.location,
        item.category,
        householdId,
        userId,
      );
    },
    onMutate: async (item) => {
      // Cancel any outgoing re-fetches so they don't overwrite the optimistic value
      await queryClient.cancelQueries({
        queryKey: ["inventory", item.location, householdId],
      });
      await queryClient.cancelQueries({
        queryKey: ["inventory", "all", householdId],
      });

      // Snapshot the previous values for rollback
      const previousByLocation = queryClient.getQueryData<InventoryItem[]>([
        "inventory",
        item.location,
        householdId,
      ]);
      const previousAll = queryClient.getQueryData<InventoryItem[]>([
        "inventory",
        "all",
        householdId,
      ]);

      // Build a temporary optimistic item
      const optimisticItem: InventoryItem = {
        id: `optimistic-${Date.now()}`,
        user_id: userId ?? "",
        household_id: householdId ?? "",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        ...item,
      };

      queryClient.setQueryData<InventoryItem[]>(
        ["inventory", item.location, householdId],
        (old) => (old ? [optimisticItem, ...old] : [optimisticItem]),
      );
      queryClient.setQueryData<InventoryItem[]>(
        ["inventory", "all", householdId],
        (old) => (old ? [optimisticItem, ...old] : [optimisticItem]),
      );

      return { previousByLocation, previousAll, location: item.location };
    },
    onError: (_err, item, context) => {
      // Roll back to the snapshot on failure
      if (context?.previousByLocation !== undefined) {
        queryClient.setQueryData(
          ["inventory", item.location, householdId],
          context.previousByLocation,
        );
      }
      if (context?.previousAll !== undefined) {
        queryClient.setQueryData(
          ["inventory", "all", householdId],
          context.previousAll,
        );
      }
    },
    onSettled: (_data, _err, item) => {
      // Re-sync from server to catch any drift
      void queryClient.invalidateQueries({
        queryKey: ["inventory", item.location, householdId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["inventory", "all", householdId],
      });
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: (id: string) => inventoryService.deleteItem(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ["inventory"] });

      // Snapshot all location caches
      const snapshots: Record<string, InventoryItem[] | undefined> = {};
      for (const loc of [
        "all",
        "fridge",
        "freezer",
        "pantry",
        "other",
      ] as const) {
        snapshots[loc] = queryClient.getQueryData<InventoryItem[]>([
          "inventory",
          loc,
          householdId,
        ]);
      }

      // Optimistically remove the item from every cache slice
      const removeItem = (old: InventoryItem[] | undefined) =>
        old ? old.filter((item) => item.id !== id) : old;
      for (const loc of [
        "all",
        "fridge",
        "freezer",
        "pantry",
        "other",
      ] as const) {
        queryClient.setQueryData(["inventory", loc, householdId], removeItem);
      }

      return { snapshots };
    },
    onError: (_err, _id, context) => {
      // Roll back all location caches
      if (context?.snapshots) {
        for (const [loc, data] of Object.entries(context.snapshots)) {
          queryClient.setQueryData(["inventory", loc, householdId], data);
        }
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["inventory"] });
    },
  });

  const updateItemMutation = useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<InventoryItem>;
    }) => inventoryService.updateItem(id, updates),
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey: ["inventory"] });

      const snapshots: Record<string, InventoryItem[] | undefined> = {};
      for (const loc of [
        "all",
        "fridge",
        "freezer",
        "pantry",
        "other",
      ] as const) {
        snapshots[loc] = queryClient.getQueryData<InventoryItem[]>([
          "inventory",
          loc,
          householdId,
        ]);
      }

      // Apply the update optimistically across all cache slices
      const applyUpdate = (old: InventoryItem[] | undefined) =>
        old
          ? old.map((item) => (item.id === id ? { ...item, ...updates } : item))
          : old;
      for (const loc of [
        "all",
        "fridge",
        "freezer",
        "pantry",
        "other",
      ] as const) {
        queryClient.setQueryData(["inventory", loc, householdId], applyUpdate);
      }

      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      if (context?.snapshots) {
        for (const [loc, data] of Object.entries(context.snapshots)) {
          queryClient.setQueryData(["inventory", loc, householdId], data);
        }
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["inventory"] });
    },
  });

  return {
    items,
    ...queryInfo,
    addItem: addItemMutation.mutateAsync,
    deleteItem: deleteItemMutation.mutateAsync,
    updateItem: updateItemMutation.mutateAsync,
  };
}
